import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type BubbleTone = 'paper' | 'pop' | 'zest' | 'ink';

const TONE: Record<BubbleTone, { box: string; tail: string }> = {
  paper: { box: 'bg-fg text-ground', tail: 'bg-fg' },
  pop: { box: 'bg-primary text-ground', tail: 'bg-primary' },
  zest: { box: 'bg-green text-night', tail: 'bg-green' },
  ink: { box: 'bg-surface text-fg', tail: 'bg-surface' },
};

/**
 * A chat-style speech bubble for mascots (SPEC_V10). `side` is where the
 * speaker sits (the tail points that way). Changing `bubbleKey` pops the
 * new line in; under reduced motion it simply fades.
 */
export function SpeechBubble({
  children,
  side = 'left',
  tone = 'paper',
  bubbleKey,
  className,
  live = false,
  testId,
}: {
  children: ReactNode;
  side?: 'left' | 'right' | 'bottom';
  tone?: BubbleTone;
  /** Swap animation key (e.g. the line's text). */
  bubbleKey?: string | number;
  className?: string;
  /** Announce changes politely to screen readers. */
  live?: boolean;
  testId?: string;
}) {
  const reduced = useReducedMotion();
  const t = TONE[tone];
  return (
    <div className={cn('relative', className)} aria-live={live ? 'polite' : undefined} data-testid={testId}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={bubbleKey ?? 'b'}
          initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.9, y: 8, filter: 'blur(4px)' }}
          animate={{ opacity: 1, scale: 1, y: 0, filter: 'blur(0px)' }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, y: -8, filter: 'blur(4px)', transition: { duration: 0.15, ease: 'easeOut' } }}
          transition={reduced ? { duration: 0.15 } : { duration: 0.3, ease: 'easeOut' }}
          style={{ originX: side === 'left' ? 0 : side === 'right' ? 1 : 0.5, originY: 1 }}
          className={cn('relative w-fit max-w-full rounded-[18px] px-3.5 py-2 text-[15px] font-medium leading-snug', t.box, side === 'right' && 'ml-auto', side === 'bottom' && 'mx-auto')}
        >
          {children}
          <span
            aria-hidden
            className={cn(
              'absolute h-3 w-3 rotate-45',
              t.tail,
              side === 'left' && '-bottom-1 left-4',
              side === 'right' && '-bottom-1 right-4',
              side === 'bottom' && '-bottom-1 left-1/2 -ml-1.5',
            )}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/**
 * Steps through `count` items every `ms` (e.g. cycling speech bubbles).
 * Pauses while the tab is hidden; `paused` freezes it (say, after a tap).
 */
export function useTicker(count: number, ms: number, paused = false, start = 0): number {
  const [i, setI] = useState(start);
  useEffect(() => {
    if (paused || count < 2) return;
    const id = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      setI((n) => (n + 1) % count);
    }, ms);
    return () => window.clearInterval(id);
  }, [count, ms, paused]);
  return i % Math.max(1, count);
}
